/**
 * OperationPickerModal.js
 *
 * Slide-up picker modal for selecting which operation to start.
 * Matches the LogPickerModal design (slide-up sheet with colored icons).
 */

import React, { useMemo } from 'react';
import { X, Play, Anchor, Users, Package, Wrench, Fuel, Ship } from 'lucide-react';
import { OPERATION_TYPE_LABELS, getOperationTypeLabels } from '../../../../../config/operationsConfig';
import { useStorageState } from '../../../../../hooks/useStorageState';

// Map operation types to icons and colors
const OPERATION_CONFIG = {
  pax_transfer: { Icon: Users, color: 'blue' },
  cargo_ops: { Icon: Package, color: 'amber' },
  bunkering: { Icon: Fuel, color: 'orange' },
  anchoring: { Icon: Anchor, color: 'slate' },
  mooring: { Icon: Anchor, color: 'emerald' },
  maintenance: { Icon: Wrench, color: 'purple' },
  towing: { Icon: Ship, color: 'cyan' },
  default: { Icon: Play, color: 'cyan' }
};

const getOperationConfig = (operationType) => {
  return OPERATION_CONFIG[operationType] || OPERATION_CONFIG.default;
};

const OperationPickerModal = ({
  isOpen,
  onClose,
  onSelect
}) => {
  // Load voyage settings to filter operations
  const { value: voyageSettings } = useStorageState('voyage_settings', null);

  // Build operation options from config, filtered by settings
  const operationOptions = useMemo(() => {
    // New: Use operationsList if available (Phase 3+)
    if (voyageSettings?.operationsList?.length > 0) {
      // Filter: only enabled mid-voyage operations (NOT phase operations)
      return voyageSettings.operationsList
        .filter(op => !op.phaseType && op.enabled !== false)
        .map(op => {
          const config = getOperationConfig(op.id);
          return {
            value: op.id,
            label: op.label,
            Icon: config.Icon,
            color: config.color
          };
        });
    }

    // Legacy fallback: Get operations from user settings (includes custom operations)
    // Falls back to OPERATION_TYPE_LABELS if no settings exist
    const labels = getOperationTypeLabels(voyageSettings);
    const allOperations = Object.entries(labels || {});
    const enabledOperations = voyageSettings?.enabledOperations;

    // If settings exist and enabledOperations is defined, filter by it
    // Otherwise show all operations (default behavior)
    const filteredOperations = enabledOperations
      ? allOperations.filter(([value]) => enabledOperations.includes(value))
      : allOperations;

    return filteredOperations.map(([value, label]) => {
      const config = getOperationConfig(value);
      return {
        value,
        label,
        Icon: config.Icon,
        color: config.color
      };
    });
  }, [voyageSettings]);

  if (!isOpen) return null;

  const handleSelect = (operationType) => {
    onClose();
    onSelect(operationType);
  };

  // Color mappings for Tailwind classes
  const colorClasses = {
    blue: { bg: 'bg-blue-50', bgHover: 'hover:bg-blue-100', icon: 'bg-blue-100', iconText: 'text-blue-600' },
    cyan: { bg: 'bg-cyan-50', bgHover: 'hover:bg-cyan-100', icon: 'bg-cyan-100', iconText: 'text-cyan-600' },
    amber: { bg: 'bg-amber-50', bgHover: 'hover:bg-amber-100', icon: 'bg-amber-100', iconText: 'text-amber-600' },
    orange: { bg: 'bg-orange-50', bgHover: 'hover:bg-orange-100', icon: 'bg-orange-100', iconText: 'text-orange-600' },
    emerald: { bg: 'bg-emerald-50', bgHover: 'hover:bg-emerald-100', icon: 'bg-emerald-100', iconText: 'text-emerald-600' },
    purple: { bg: 'bg-purple-50', bgHover: 'hover:bg-purple-100', icon: 'bg-purple-100', iconText: 'text-purple-600' },
    slate: { bg: 'bg-slate-50', bgHover: 'hover:bg-slate-100', icon: 'bg-slate-200', iconText: 'text-slate-600' }
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/40 z-40"
        onClick={onClose}
      />

      {/* Slide-up Panel */}
      <div className="fixed bottom-0 left-0 right-0 z-50 animate-slide-up">
        <div className="bg-white rounded-t-2xl shadow-xl border-t border-slate-200 max-w-lg mx-auto safe-area-bottom">
          {/* Handle bar */}
          <div className="flex justify-center pt-3 pb-2">
            <div className="w-10 h-1 bg-slate-300 rounded-full"></div>
          </div>

          {/* Header */}
          <div className="flex items-center justify-between px-5 pb-3 border-b border-slate-100">
            <h3 className="font-semibold text-slate-900">Start Operation</h3>
            <button
              onClick={onClose}
              className="p-2 -mr-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-full transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Options Grid */}
          <div className="p-4 grid grid-cols-3 gap-3">
            {operationOptions.length === 0 ? (
              <p className="col-span-3 text-sm text-slate-500 p-4 text-center">
                No operations configured
              </p>
            ) : (
              operationOptions.map((op) => {
                const IconComponent = op.Icon;
                const colors = colorClasses[op.color] || colorClasses.cyan;
                return (
                  <button
                    key={op.value}
                    onClick={() => handleSelect(op.value)}
                    className={`flex flex-col items-center gap-2 p-4 ${colors.bg} ${colors.bgHover} rounded-xl transition-colors`}
                  >
                    <div className={`w-12 h-12 rounded-full ${colors.icon} flex items-center justify-center`}>
                      <IconComponent className={`h-6 w-6 ${colors.iconText}`} />
                    </div>
                    <span className="text-sm font-medium text-slate-700 text-center">
                      {op.label}
                    </span>
                  </button>
                );
              })
            )}
          </div>

          {/* Safe area spacing for iOS */}
          <div className="h-6"></div>
        </div>
      </div>

      {/* Animation styles */}
      <style>{`
        @keyframes slide-up {
          from {
            transform: translateY(100%);
          }
          to {
            transform: translateY(0);
          }
        }
        .animate-slide-up {
          animation: slide-up 0.3s cubic-bezier(0.16, 1, 0.3, 1);
        }
        .safe-area-bottom {
          padding-bottom: env(safe-area-inset-bottom, 0);
        }
      `}</style>
    </>
  );
};

export default OperationPickerModal;
